import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import type {
  BatchStatus,
  BatchStatusResponse,
  CreatorQuery,
} from "./types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const REFRESH_INTERVAL_MS = 30_000;

/** What counts as progress for a batch: any change in its funnel or sourcing. */
function fingerprint(batch: BatchStatus): string {
  return JSON.stringify([
    batch.status_counts,
    batch.sourcing.jobs.map((job) => [job.status, job.inserted, job.found]),
  ]);
}

/**
 * Polls POST /api/sourcing/batchStatus every 30 s while the tab is visible
 * (and right away when it becomes visible again). Besides the payload it
 * tracks, across refreshes, when each batch last changed and since when the
 * analysis fleet has been holding more backlog than its floor absorbs —
 * neither is in a single response.
 */
export function useBatchStatus(
  batchNames: string[],
  creatorQueries: Record<string, CreatorQuery>,
  enabled: boolean
) {
  const { getToken } = useAuth();
  const [data, setData] = useState<BatchStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  /** batch_name → when its fingerprint last changed (ms). */
  const [lastChange, setLastChange] = useState<Record<string, number>>({});
  const [fleetStuckSince, setFleetStuckSince] = useState<number | null>(null);

  const fingerprints = useRef(new Map<string, string>());
  const lastRefreshRef = useRef(0);
  // Responses can land out of order (a slow refresh after a page flip);
  // only the newest request may write.
  const seq = useRef(0);
  // The interval reads the latest inputs without restarting on every render.
  const inputs = useRef({ batchNames, creatorQueries });
  inputs.current = { batchNames, creatorQueries };
  const inputsKey = JSON.stringify([batchNames, creatorQueries]);

  const refresh = useCallback(async () => {
    const mySeq = ++seq.current;
    lastRefreshRef.current = Date.now();
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/api/sourcing/batchStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          batch_names: inputs.current.batchNames,
          creators: inputs.current.creatorQueries,
        }),
      });
      const payload = (await response.json()) as BatchStatusResponse & {
        message?: string;
      };
      if (mySeq !== seq.current) return;
      if (!response.ok || !payload.success) {
        throw new Error(
          payload.error || payload.message || `Request failed (${response.status})`
        );
      }

      const now = Date.now();
      const changed: Record<string, number> = {};
      for (const batch of payload.batches) {
        const next = fingerprint(batch);
        if (fingerprints.current.get(batch.batch_name) !== next) {
          fingerprints.current.set(batch.batch_name, next);
          changed[batch.batch_name] = now;
        }
      }
      if (Object.keys(changed).length) {
        setLastChange((previous) => ({ ...previous, ...changed }));
      }

      const fleet = payload.fleet;
      const stuck = Boolean(
        fleet?.available &&
          fleet.analysis.backlog > fleet.autoscaler.floor_capacity &&
          fleet.analysis.in_flight <= fleet.autoscaler.floor_replicas
      );
      setFleetStuckSince((since) => (stuck ? since ?? now : null));

      setData(payload);
      setError(null);
      setRefreshedAt(now);
    } catch (e) {
      if (mySeq !== seq.current) return;
      setError((e as Error).message);
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, REFRESH_INTERVAL_MS);
    const onVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastRefreshRef.current >= REFRESH_INTERVAL_MS
      ) {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // inputsKey: a new batch list or creator page fetches right away.
  }, [enabled, inputsKey, refresh]);

  return { data, error, loading, refreshedAt, lastChange, fleetStuckSince, refresh };
}
